/** Theme choice stored in user_preference('app').theme, defaulting to the ui.default_theme setting (spec §10.1). */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { api, unwrap } from './api/client'
import { useMe } from './auth'
import { type PickerStyle, isPickerStyle } from './pickerStyle'
import { type ThemeChoice, applyTheme, isThemeChoice } from './theme'

const PREF_KEY = ['preference', 'app'] as const
type AppPreference = { theme?: ThemeChoice; picker?: PickerStyle; [key: string]: unknown }

export function useThemePreference() {
  const me = useMe()
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: PREF_KEY,
    queryFn: async () =>
      (unwrap(await api.GET('/api/v1/me/preferences/{screen_key}/', { params: { path: { screen_key: 'app' } } })).value ??
        {}) as AppPreference,
    staleTime: Infinity,
  })
  const fallback = me.settings['ui.default_theme']
  const choice: ThemeChoice = isThemeChoice(data?.theme) ? data.theme : isThemeChoice(fallback) ? fallback : 'light'

  useEffect(() => {
    if (data) applyTheme(choice)
  }, [choice, data])

  const setChoice = useCallback(
    (next: ThemeChoice) => {
      const value = { ...(queryClient.getQueryData<AppPreference>(PREF_KEY) ?? {}), theme: next }
      queryClient.setQueryData(PREF_KEY, value)
      applyTheme(next)
      void api.PUT('/api/v1/me/preferences/{screen_key}/', { params: { path: { screen_key: 'app' } }, body: { value } })
    },
    [queryClient],
  )

  return { choice, setChoice }
}

/** How pick-one dropdowns look for this person (user_preference('app').picker); Standard until they choose. */
export function usePickerStyle() {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: PREF_KEY,
    queryFn: async () =>
      (unwrap(await api.GET('/api/v1/me/preferences/{screen_key}/', { params: { path: { screen_key: 'app' } } })).value ??
        {}) as AppPreference,
    staleTime: Infinity,
  })
  const choice: PickerStyle = isPickerStyle(data?.picker) ? data.picker : 'standard'
  const setChoice = useCallback(
    (next: PickerStyle) => {
      const value = { ...(queryClient.getQueryData<AppPreference>(PREF_KEY) ?? {}), picker: next }
      queryClient.setQueryData(PREF_KEY, value)
      void api.PUT('/api/v1/me/preferences/{screen_key}/', { params: { path: { screen_key: 'app' } }, body: { value } })
    },
    [queryClient],
  )
  return { choice, setChoice }
}
